/**
 * @fileoverview AudioStreamer class
 *
 * @example
 * // Usage:
 * import { AudioStreamer } from './AudioStreamer'
 *
 * function Component() {
 *   const audioStreamer = new AudioStreamer()
 *   // ...
 * }
 */

import { EventEmitter } from 'eventemitter3'
import { Deferred } from './Deferred'
import { MediaDevice } from './MediaDevice'
import { WebRTCStreamer } from './WebRTCStreamer'

/**
 * AudioContext in old browsers (e.g. Safari <14) can only be obtained using the webkit prefix
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AudioContext = window.AudioContext || (window as any).webkitAudioContext

/**
 * @class AudioStreamer
 * @description Class for streaming audio
 */
export class AudioStreamer {
    /**
     * Reference to the local MediaStream
     */
    #stream: MediaStream

    /**
     * Reference/tracker for the remote MediaStream
     */
    #isTrackReady = new Deferred<MediaStream>()

    /**
     * Streaming indicator. Prevents multiple requests for streaming
     */
    #isSteraming: Deferred<boolean>

    /**
     * Reference to the MediaRecorder
     */
    #streamer: WebRTCStreamer

    /**
     * Object that wraps navigator.getUserMedia to reduce boilerplate
     */
    #mediaDevice: MediaDevice

    /**
     * RCTConfiguration for the WebRTCStreamer
     */
    #rtcConfig: RTCConfiguration

    /**
     * Promise to indicate the state of preparing
     */
    #isPreparing = new Deferred<void>()

    /**
     * Event Emitter to communicate back the "track" event
     */
    ee = new EventEmitter()

    /**
     * Constructor for the AudioStreamer class
     * @param {RTCConfiguration} config - The RTC configuration
     */
    constructor(config: RTCConfiguration) {
        this.#mediaDevice = new MediaDevice()
        this.#rtcConfig = config
    }

    /**
     * Creates a MediaStream for audio recording
     * @returns {Promise<MediaStream>} The MediaStream
     */
    private async createMediaStream(): Promise<MediaStream> {
        if (!this.#mediaDevice.isSupportingAudioRecording()) {
            throw Error('mediaDevices or getUserMedia method is not supported.')
        }

        try {
            return this.#mediaDevice.getStream()
        } catch (e: unknown) {
            throw this.#mediaDevice.processMediaDeviceError(e as Error)
        }
    }

    /**
     * Gets the stream analyser
     * @returns {AnalyserNode | null} The stream analyser
     */
    getStreamAnalyser(): AnalyserNode {
        if (!this.#stream) {
            return null
        }

        const audioCtx = new AudioContext()
        const audioSource = audioCtx.createMediaStreamSource(this.#stream)
        const analyser = audioCtx.createAnalyser()

        audioSource.connect(analyser)

        analyser.fftSize = 32

        return analyser
    }

    /**
     * Connects to the WebRTCStreamer
     * @returns {Promise<void>}
     */
    async connect(): Promise<void> {
        try {
            this.#stream = await this.createMediaStream()
            this.#streamer = new WebRTCStreamer(this.#stream, this.#rtcConfig)

            this.#streamer.ee
                .addListener('connected', () => {
                    this.ee.emit('connected')
                })
                .addListener('disconnected', () => {
                    this.ee.emit('disconnected')
                })
                .addListener('track', (stream: MediaStream) => {
                    this.#isTrackReady.resolve(stream)
                    this.#isSteraming = undefined
                })
                .addListener('track.start', () => {
                    this.ee.emit('track.start')
                })
                .addListener('track.end', () => {
                    this.ee.emit('track.end')
                })

            this.#isPreparing.resolve()
        } catch (e: unknown) {
            this.#isPreparing.reject(e)
            this.ee.emit('error', e)
        }
    }

    /**
     * Plays an audio file
     * @param {string} audio - The audio file to play
     * @returns {Promise<MediaStream>} The MediaStream
     */
    async play(audio: string): Promise<MediaStream> {
        if (!this.#isSteraming) {
            await this.#isPreparing.promise
            this.#isSteraming = new Deferred()
            this.#streamer.play(audio)
            this.#isSteraming.resolve(true)
        }
        return await this.#isTrackReady.promise
    }

    /**
     * Starts audio recording
     * @returns {Promise<string | null>} Resolves with the recording UUID if the audio streaming process has been started, otherwise null
     */
    async start(): Promise<string> {
        await this.#isPreparing.promise

        try {
            const recording = await this.#streamer.start()
            return recording
        } catch (e: unknown) {
            console.error('Failed to establish a streaming connection')
        }
    }

    /**
     * Skips the recording
     * @returns {Promise<void>}
     */
    async skip(): Promise<void> {
        await this.#isPreparing.promise

        await this.#streamer.skip()
    }

    /**
     * Stops the recording
     * @returns void
     */
    async stop(): Promise<void> {
        if (!this.#streamer) {
            throw Error('There are no active recording at the time')
        }

        await this.#streamer.stop()

        console.debug('streaming has stopped')

        /**
         * Stop all tracks within the stream to remove the recordinig indication
         */
        this.#stream.getTracks().forEach((track: MediaStreamTrack) => track.stop())

        /**
         * Notify that the connection is closed or is about to be closed
         */
        this.#streamer.ee.emit('disconnected')

        /**
         * Reset AudioStreamer state
         */
        this.#streamer.ee.removeAllListeners()
        this.#streamer = undefined
        this.#stream = undefined
    }
}
